import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { AuthService } from '../_service/auth.service';

// The login/register calls handle their own 401/409 ("invalid credentials" /
// "username taken") — don't force a logout/redirect loop off the back of a
// failed login or registration attempt.
const SKIP_ENDPOINTS = ['/login', '/register'];

/** A 401 from an authenticated endpoint, while a token IS present, means that
 * token is expired, tampered with, or the account was revoked server-side —
 * the correct response is to force re-authentication rather than let the app
 * keep running in a silently-broken, half-authenticated state.
 *
 * Only act when a token was actually attached: an unauthenticated page (the
 * login/register screens, or the brief window before login) still triggers
 * background calls to authenticated endpoints, and those getting an expected
 * 401 must NOT force-navigate away from wherever the user currently is. */
export const unauthorizedInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse && err.status === 401 && auth.getToken()) {
        const url = req.url ?? '';
        const shouldSkip = SKIP_ENDPOINTS.some((e) => url.includes(e));
        if (!shouldSkip) {
          auth.logout();
        }
      }
      return throwError(() => err);
    }),
  );
};
